import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, useNavigate } from '@tanstack/react-router';
import { Card, Pagination, SortableHeader, Table, TableColumnsMenu, TextLink, cn, useTableColumns, } from '@wit-support/ui';
import { intRu, gapMinor, money, pct, pct100, moneyWhole } from '../format.js';
import { columnHint } from '../glossary.js';
import { accountName, hasActivity } from '../summary-logic.js';
import { menuSort, paybackColumnsKey } from '../table-columns.js';
import { pageOf, sortBy, verdictRank } from '../table-sort.js';
import { linkTo, useTableUrl } from '../url-state.js';
import { ChanceCell, CppCell, GapCell } from './money-cells.js';
import { StripCell, stripMax } from './strip-cell.js';
import { GroupVerdictBadge } from './verdict-badge.js';
export const ACCOUNT_SORT_KEYS = [
    'name',
    'spend',
    'purchases',
    'cpp',
    'breakeven',
    'roas',
    'p',
    'gap',
    'verdict',
];
const ACCOUNT_COLUMN_LABEL = {
    name: 'Кабинет',
    spend: 'Расход',
    purchases: 'Покупки',
    cpp: 'Цена покупки',
    breakeven: 'В ноль до',
    roas: 'Вернётся на 1 $',
    p: 'Шанс',
    gap: 'Не хватает / запас',
    verdict: 'Вывод',
};
/** Колонки «Кабинетов» Сводки: «Кабинет» закреплён и всегда виден. */
export const ACCOUNT_COLUMN_DEFS = ACCOUNT_SORT_KEYS.map((id) => ({
    id,
    label: ACCOUNT_COLUMN_LABEL[id],
    ...(id === 'name' ? { defaultPin: 'left', hideable: false } : {}),
}));
const LEFT = new Set(['name', 'roas', 'verdict']);
const HEADER_TITLE = {
    breakeven: 'Подсказка у числа — «уверенно до»',
    roas: 'Точка — ожидание, полоса — «скорее всего от … до», пунктир — порог',
};
const MOMENT_WORD = {
    t0: 'сразу при покупке',
    t24: 'через сутки',
    d14: 'на 14-й день',
    mixed: 'самый свежий для каждой покупки',
};
/**
 * «Кабинеты» Сводки (спека §6.1 п.4): строки `accountsBreakdown` с расходом,
 * клик — Кампании этого кабинета за тот же период. Кабинеты без расхода —
 * строкой под таблицей.
 */
export function AccountsTable({ rows, theta, names, absent, period, search, }) {
    const navigate = useNavigate();
    const t = useTableUrl('acc', ACCOUNT_SORT_KEYS, { key: 'spend', dir: 'desc' });
    const active = rows.filter(hasActivity);
    const valueOf = (a, k) => {
        switch (k) {
            case 'name':
                return accountName(a, names);
            case 'spend':
                return a.spendMinorFull;
            case 'purchases':
                return a.purchases;
            case 'cpp':
                return a.cpp;
            case 'breakeven':
                return a.cppBreakevenMinor;
            case 'roas':
                return a.answer.roas3;
            case 'p':
                return a.answer.verdict === null ? null : a.answer.p;
            case 'gap':
                // Как в «Кампаниях»: без вывода («мало покупок») — в конец.
                return a.answer.verdict === null ? null : gapMinor(a, theta);
            case 'verdict':
                return verdictRank(a.answer.verdict);
        }
    };
    const sorted = sortBy(active, t.sort, valueOf);
    const shown = pageOf(sorted, t.page, t.size);
    const max = stripMax(active, 2.2);
    const link = (id) => linkTo('campaigns', { from: period.from, to: period.to, accounts: id }, search);
    const cols = useTableColumns(paybackColumnsKey('accounts'), ACCOUNT_COLUMN_DEFS);
    const span = cols.visible.length;
    const cellOf = (k, a) => {
        switch (k) {
            case 'name': {
                const l = link(a.accountId);
                return (_jsx(TextLink, { asChild: true, children: _jsx(Link, { to: l.to, search: l.search, onClick: (e) => e.stopPropagation(), title: `Момент: ${MOMENT_WORD[a.answer.momentUsed] ?? '—'} · покрытие ${pct100(a.answer.coveragePct)}`, className: "font-semibold", children: accountName(a, names) }) }));
            }
            case 'spend':
                return (_jsxs(_Fragment, { children: [moneyWhole(a.spendMinorFull), ' ', _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: pct(a.spendShare) })] }));
            case 'purchases':
                return intRu(a.purchases);
            case 'cpp':
                return _jsx(CppCell, { cpp: a.cpp, breakeven: a.cppBreakevenMinor });
            case 'breakeven':
                return money(a.cppBreakevenMinor);
            case 'roas':
                return _jsx(StripCell, { group: a, theta: theta, max: max });
            case 'p':
                return _jsx(ChanceCell, { p: a.answer.p, verdict: a.answer.verdict });
            case 'gap':
                return _jsx(GapCell, { gap: gapMinor(a, theta), verdict: a.answer.verdict });
            case 'verdict':
                return _jsx(GroupVerdictBadge, { group: a });
        }
    };
    const cellClass = (k) => {
        switch (k) {
            case 'spend':
                return 'whitespace-nowrap text-right tabular-nums';
            case 'purchases':
                return 'text-right tabular-nums';
            case 'breakeven':
                return 'text-right text-[var(--color-text-muted)] tabular-nums';
            case 'roas':
                return 'w-[260px]';
            case 'cpp':
            case 'p':
            case 'gap':
                return 'text-right';
            default:
                return undefined;
        }
    };
    return (_jsxs(Card, { className: "px-4 pb-1.5 pt-3", "aria-label": "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2 px-1 pb-1", children: [_jsx("h2", { className: "text-sm font-semibold", children: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B" }), _jsx("span", { className: "flex-1" }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u043A\u043B\u0438\u043A \u2014 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430" }), _jsx(TableColumnsMenu, { columns: cols, sort: menuSort(t.sort, ACCOUNT_COLUMN_DEFS) })] }), _jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((col) => (_jsx(SortableHeader, { column: col, sortKey: col.id, sort: t.sort, onSortChange: t.setSort, align: LEFT.has(col.id) ? 'left' : 'right', defaultDirection: col.id === 'name' ? 'asc' : 'desc', className: "whitespace-nowrap", title: HEADER_TITLE[col.id], hint: columnHint(col.id, theta), children: col.def.label }, col.id))) }) }), _jsxs(Table.Body, { children: [shown.map((a) => {
                                const l = link(a.accountId);
                                return (_jsx(Table.Row, { className: "cursor-pointer", "data-testid": `account-row-${a.accountId}`, onClick: () => void navigate({ to: l.to, search: l.search }), children: cols.visible.map((col) => (_jsx(Table.Cell, { column: col, className: cn(cellClass(col.id)), title: col.id === 'breakeven' && a.cppSafeMinor !== null
                                            ? `уверенно до ${money(a.cppSafeMinor)}`
                                            : undefined, children: cellOf(col.id, a) }, col.id))) }, a.accountId));
                            }), active.length === 0 ? (_jsx(Table.Empty, { colSpan: span, children: "\u0417\u0430 \u043F\u0435\u0440\u0438\u043E\u0434 \u043D\u0435\u0442 \u0440\u0430\u0441\u0445\u043E\u0434\u0430 \u043D\u0438 \u0432 \u043E\u0434\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435" })) : null, absent.length ? (_jsx(Table.Row, { className: "hover:bg-transparent", children: _jsxs(Table.Cell, { colSpan: span, className: "text-xs text-[var(--color-text-muted)]", "data-testid": "absent-accounts", children: [absent.join(', '), " \u2014 \u0431\u0435\u0437 \u0440\u0430\u0441\u0445\u043E\u0434\u0430 \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434"] }) })) : null] })] }), sorted.length > t.size ? (_jsx(Pagination, { page: t.page, pageSize: t.size, total: sorted.length, onPageChange: t.setPage, onPageSizeChange: t.setSize, className: "px-1 pt-2" })) : null] }));
}
//# sourceMappingURL=accounts-table.js.map